import React, { useState, useEffect } from 'react';
import { admin } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Clock, ChevronLeft, ChevronRight, Shield, UserCog, Ban, Trash2, Gift, Bell, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_MAP = {
  role_change: { label: 'Changement rôle', icon: UserCog, color: 'text-blue-400' },
  arcanien_toggle: { label: 'Toggle Arcanien', icon: Shield, color: 'text-yellow-400' },
  arcanien_gift: { label: 'Cadeau Arcanien', icon: Gift, color: 'text-teal-400' },
  user_ban: { label: 'Bannissement', icon: Ban, color: 'text-red-400' },
  user_unban: { label: 'Débannissement', icon: Ban, color: 'text-green-400' },
  user_delete: { label: 'Suppression', icon: Trash2, color: 'text-red-400' },
  report_update: { label: 'Signalement traité', icon: AlertTriangle, color: 'text-orange-400' },
  popup_create: { label: 'Popup créé', icon: Bell, color: 'text-purple-400' },
};

export default function ActivityLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const limit = 20;

  useEffect(() => { load(); }, [page, actionFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (actionFilter) params.action = actionFilter;
      const res = await admin.getActivityLogs(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="logs-title">Journal d'activité</h1>
          <p className="text-[#666] mt-1">{total} action{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''}</p>
        </div>
      </div>

      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardContent className="p-4">
          <Select value={actionFilter || 'all'} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setPage(0); }}>
            <SelectTrigger className="w-full md:w-64 bg-[#252525] border-[#333] text-white" data-testid="logs-action-filter">
              <SelectValue placeholder="Toutes les actions" />
            </SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="all" className="text-white">Toutes les actions</SelectItem>
              {Object.entries(ACTION_MAP).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-[#666]">Aucune activité enregistrée</div>
          ) : (
            <div className="divide-y divide-[#333]">
              {logs.map((log) => {
                const action = ACTION_MAP[log.action] || { label: log.action, icon: Clock, color: 'text-[#666]' };
                const Icon = action.icon;
                return (
                  <div key={log.id} className="flex items-center gap-4 p-4 hover:bg-[#252525] transition-colors">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${action.color} bg-current/10`}
                      style={{ backgroundColor: `color-mix(in srgb, currentColor 10%, transparent)` }}>
                      <Icon className={`w-5 h-5 ${action.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-[#333] text-[#B0B0B0] text-xs">{action.label}</Badge>
                        <span className="text-white text-sm font-medium">{log.admin_name}</span>
                      </div>
                      <p className="text-[#B0B0B0] text-sm mt-0.5 truncate">{log.details}</p>
                    </div>
                    <div className="text-[#666] text-xs flex-shrink-0 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between p-4 border-t border-[#333]">
            <p className="text-[#666] text-sm">Page {page + 1} sur {totalPages || 1}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-[#333] text-white hover:bg-[#333]"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="border-[#333] text-white hover:bg-[#333]"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
